import React from 'react';
import {Map} from 'immutable';
import keyBy from 'lodash/keyBy';
import countOccurrences from 'utils/countOccurrences';
import moment from 'moment';
import SAWHeader from './SAWHeader';
import {
  TIMEFRAME_OPTIONS,
  toggleSortDirection,
  reduceScores,
  sortCompare,
  getPeriodDays,
  sortSkills,
} from '../Common';
import SAWBody from './SAWBody';

import './StrengthsAndWeaknesses.styl';
import WidgetBase from '../WidgetBase';

import SkillActions from 'actions/SkillActions';
import ManagerDashboardSource from 'sources/ManagerDashboardSource';
import alert from 'utils/alert';
import SAWScorecardTabs from 'components/manager-dashboard/strengths-and-weaknesses/SAWScorecardTabs';
import Team from 'records/Team';
import {HISTORY_ACTIONS} from 'components/StatsPage2.utils';
import {SKILLS_ORDER_OPTIONS} from 'components/manager-dashboard/strengths-and-weaknesses/SKILLS_ORDER_OPTIONS';

class StrengthsAndWeaknessesByTeam extends WidgetBase {
  constructor(props) {
    super(props);
    this.nameClick = this.nameClick.bind(this);
    this.totalsClick = this.totalsClick.bind(this);
    this.headerClick = this.headerClick.bind(this);
    this.updatePeriod = this.updatePeriod.bind(this);
    this.skillsOrderChange = this.skillsOrderChange.bind(this);
    this.scorecardOptionChange = this.scorecardOptionChange.bind(this);
    this.scoresSelectedOptionChange = this.scoresSelectedOptionChange.bind(this);
    this.callStageSelectedOptionChange = this.callStageSelectedOptionChange.bind(this);
    this.headerTooltip = this.headerTooltip.bind(this);
    this.rowTooltip = this.rowTooltip.bind(this);
    // Default is 4 weeks.
    const defaultTimeframe = TIMEFRAME_OPTIONS[3];
    this.state = {
      sortColumn: 'average-score',
      sortDirection: 'desc',
      selectedTimeframe: defaultTimeframe,
      period: getPeriodDays(defaultTimeframe),
      skillsOrder: SKILLS_ORDER_OPTIONS.by_count.value,
    };
  }

  /**
   * Event handler for when the team name is clicked.
   * @param item row item
   */
  nameClick(item) {
    if (
      this.props.managers === undefined ||
      this.props.managers.find(m => m.id === item.managerId || m.userId === item.managerId)
    ) {
      const state = {selectedTeam: new Team({...item})};
      if (this.props.fromPath) {
        state.pathname = this.props.fromPath;
        state.action = HISTORY_ACTIONS.PUSH;
      }
      this.getDashboardForManager(item.managerId, this.props.adminId, 'rep-skills', state);
    } else {
      this.alertManagerInactive(item);
    }
  }

  /**
   * Event handler for when the totals for the row is clicked.
   * @param item row item
   */
  totalsClick(item) {
    const scoredCallsQuery = {
      period: this.state.period,
      startDate: this.props.startDate,
      endDate: this.props.endDate,
      scorecard: this.props.scorecardSelectedOption,
      scoresOption: this.props.scoresSelectedOption,
      isTeamId: true,
      reviewedId: item.id,
    };
    ManagerDashboardSource.getScoredCallIds(scoredCallsQuery);
  }

  /**
   * Event handler for when the header is clicked.
   * @param column
   */
  headerClick(column) {
    if (this.state.sortColumn !== column) {
      this.setState({
        sortColumn: column,
        sortDirection: 'desc',
      });
    } else {
      this.setState({
        sortDirection: toggleSortDirection(this.state.sortDirection),
      });
    }
  }

  headerTooltip(skill) {
    const {scoresSelectedOption, scorecardSelectedOption} = this.props;
    SkillActions.fetchTeamTrend({
      skillName: skill,
      from: this.props.startDate || moment().subtract(3, 'month'),
      to: this.props.endDate || moment().add(1, 'day'),
      scoredOption: scoresSelectedOption,
      scorecardId: scorecardSelectedOption,
    });
    return true;
  }

  rowTooltip(team) {
    const {scoresSelectedOption, scorecardSelectedOption} = this.props;
    if (!team.managerId) {
      alert(`Team ${team.name} must have a manager in order to view the trend`);
      return false;
    }
    SkillActions.fetchTeamTrend({
      teamId: team.id,
      from: this.props.startDate || moment().subtract(3, 'month'),
      to: this.props.endDate || moment().add(1, 'day'),
      scoredOption: scoresSelectedOption,
      scorecardId: scorecardSelectedOption,
    });
    return true;
  }

  /**
   * Depending on the "column" compare two team's stats
   * @param teamA TeamInfo
   * @param teamB TeamInfo
   * @param scores Map of team scores.  {teamId: {skills: {},}, teamId: {skills: {}}}
   * @param sortColumn field to sort by
   * @param sortDirection asc / desc
   */
  compareTeams(teamA, teamB, scores, sortColumn, sortDirection) {
    if (sortColumn === null) {
      return 0;
    }
    if (sortColumn === 'rep-name') {
      return sortCompare(teamA.name, teamB.name, sortDirection);
    }
    const teamAScores = scores.get(teamA.id);
    const teamBScores = scores.get(teamB.id);
    if (!teamAScores || !teamBScores) {
      return 0;
    }
    if (sortColumn === 'average-score') {
      return sortCompare(teamAScores.average, teamBScores.average, sortDirection);
    } else if (sortColumn === 'scorecard-count') {
      return sortCompare(teamAScores.scorecardCount, teamBScores.scorecardCount, sortDirection);
    } else {
      // sort by the skill average
      const skillTeamA = teamAScores.skillScores[sortColumn];
      const averageTeamA = skillTeamA ? skillTeamA.skillAverage : null;
      const skillTeamB = teamBScores.skillScores[sortColumn];
      const averageTeamB = skillTeamB ? skillTeamB.skillAverage : null;
      return sortCompare(averageTeamA, averageTeamB, sortDirection);
    }
  }

  renderEmptyBody() {
    return (
      <div className="empty-widget">
        Your managers have not scored any calls in the selected period. Please read our guide on how
        to effectively use scoring to encourage a coaching culture in your company. Contact your
        account manager to learn about best practices.
      </div>
    );
  }

  /**
   * the date range has changed.  Let's reload data
   * @param selectedTimeframe
   */
  updatePeriod(selectedTimeframe) {
    const period = getPeriodDays(selectedTimeframe);
    this.setState({
      selectedTimeframe,
      period,
    });
    this.getWidgetData({
      widgetId: this.props.widgetId,
      period,
      scorecard: this.props.scorecardSelectedOption,
      callStage: this.props.callStageSelectedOption,
      scoresOption: this.props.scoresSelectedOption,
      widgetType: this.props.widgetType,
    });
  }

  /**
   * New Scorecard was selected
   * @param scorecard Scorecard object
   */
  scorecardOptionChange(scorecard) {
    if (this.props.scorecardOptionChange) {
      this.props.scorecardOptionChange(scorecard);
      return;
    }
    this.getWidgetData({
      widgetId: this.props.widgetId,
      period: this.state.period,
      scorecard: scorecard,
      callStage: this.props.callStageSelectedOption,
      scoresOption: this.props.scoresSelectedOption,
      widgetType: this.props.widgetType,
    });
  }

  /**
   * New scores type was selected
   * @param selection
   */
  scoresSelectedOptionChange(selection) {
    if (this.props.scoresSelectedOptionChange) {
      this.props.scoresSelectedOptionChange(selection);
      return;
    }
    this.getWidgetData({
      widgetId: this.props.widgetId,
      period: this.state.period,
      scorecard: this.props.scorecardSelectedOption,
      callStage: this.props.callStageSelectedOption,
      scoresOption: selection,
      widgetType: this.props.widgetType,
    });
  }

  /**
   * new call stage was selected
   * @param selection
   */
  callStageSelectedOptionChange(selection) {
    if (this.props.callStageSelectedOptionChange) {
      this.props.callStageSelectedOptionChange(selection);
      return;
    }
    this.getWidgetData({
      widgetId: this.props.widgetId,
      period: this.state.period,
      scorecard: this.props.scorecardSelectedOption,
      callStage: selection,
      scoresOption: this.props.scoresSelectedOption,
      widgetType: this.props.widgetType,
    });
  }

  skillsOrderChange(value) {
    this.setState({
      skillsOrder: value,
    });
  }

  scoresById() {
    return Map(
      this.props.scores.map(scoreObj => {
        const {skillScores, ...otherProps} = scoreObj;
        // turn the list of scored skills into a map of skill: [score,...]
        const keyedSkills = keyBy(skillScores, item => item.skill);
        const avg = reduceScores(keyedSkills);
        return [scoreObj.teamId, {skillScores: keyedSkills, average: avg, ...otherProps}];
      })
    );
  }

  renderBodyWithData(scores, scorecardsCount, teams, sortColumn, sortDirection) {
    // turn the list of scores into a map of id: [scores,...]
    const scoresById = this.scoresById();

    const sortedTeams = teams.filter(team => scoresById.has(team.id));
    sortedTeams.sort((a, b) => this.compareTeams(a, b, scoresById, sortColumn, sortDirection));

    const displayTeams = sortedTeams.map(item => {
      return {
        ...item,
        displayName: item.name,
      };
    });

    // Convert teamMembers from an array to a mapping of id's to teams
    const teamsById = Map(displayTeams.map(member => [member.id, member]));

    const teamOrder = displayTeams
      .filter(member => scoresById.has(member.id))
      .map(member => member.id);

    const skills = countOccurrences(
      scores.flatMap(teamScore => teamScore.skillScores.map(skill => skill.skill))
    );

    const skillsOrder = sortSkills(
      Object.keys(skills),
      skills,
      this.state.skillsOrder === SKILLS_ORDER_OPTIONS.by_name.value
    );

    return (
      <SAWBody
        {...{teamOrder, skillsOrder, sortDirection, sortColumn}}
        location={this.props.location}
        startDate={this.props.startDate}
        endDate={this.props.endDate}
        members={teamsById}
        scores={scoresById}
        scorecardsCount={scorecardsCount}
        nameClick={this.nameClick}
        totalsClick={this.totalsClick}
        itemLabel="Team"
        headerClick={this.headerClick}
        headerTooltip={this.headerTooltip}
        rowTooltip={this.rowTooltip}
        skillsOrderChange={this.skillsOrderChange}
        skillsOrderSelected={this.state.skillsOrder}
      />
    );
  }

  render() {
    const {scores = [], teams = [], scorecardsCount, useScorecardTabs} = this.props;
    const {sortColumn, sortDirection, selectedTimeframe} = this.state;
    const isEmpty = scores.length === 0 || teams.length === 0;
    return (
      <div className="widget strengths-and-weaknesses by-team">
        <SAWHeader
          {...this.props}
          selectedTimeframe={selectedTimeframe}
          updatePeriod={this.updatePeriod}
          scorecardOptionChange={this.scorecardOptionChange}
          scoresSelectedOptionChange={this.scoresSelectedOptionChange}
          callStageSelectedOptionChange={this.callStageSelectedOptionChange}
          useScorecardTabs={useScorecardTabs}
          adminView
        />
        {useScorecardTabs && (
          <SAWScorecardTabs
            scorecardOptions={this.props.scorecardOptions}
            scorecardOptionChange={this.scorecardOptionChange}
          />
        )}
        {isEmpty
          ? this.renderEmptyBody()
          : this.renderBodyWithData(scores, scorecardsCount, teams, sortColumn, sortDirection)}
      </div>
    );
  }
}

StrengthsAndWeaknessesByTeam.displayName = 'StrengthsAndWeaknessesByTeam';
export default StrengthsAndWeaknessesByTeam;
